const HOVER_BASE_STYLE_KEY = '__echartsExtensionHoverBaseStyle';
const HOVER_DIM_OPACITY_KEY = '__echartsExtensionHoverDimOpacity';
const HOVER_ENTERING_KEY = '__echartsExtensionHoverEntering';
const DEFAULT_HOVER_DIM_OPACITY = 0.12;
const DEFAULT_HOVER_TRANSITION_DURATION = 180;
const DEFAULT_HOVER_TRANSITION_EASING = 'cubicOut';
const HOVER_TRANSITION_SCOPE = 'element-hover';
export function installElementHover(items, options = {}) {
    const hoverItems = items
        .map((item) => ({
        elements: uniqueElements(item.elements),
        triggerElements: uniqueElements(item.triggerElements?.length ? item.triggerElements : item.elements)
    }))
        .filter((item) => item.elements.length && item.triggerElements.length);
    if (!hoverItems.length)
        return undefined;
    const dimOpacity = finiteNumber(options.dimOpacity, DEFAULT_HOVER_DIM_OPACITY);
    const transitionDuration = finiteNumber(options.transitionDuration, DEFAULT_HOVER_TRANSITION_DURATION);
    const transitionEasing = typeof options.transitionEasing === 'string' && options.transitionEasing
        ? options.transitionEasing
        : DEFAULT_HOVER_TRANSITION_EASING;
    const isEnabled = createEnabledPredicate(options.enabled);
    const hoverTargets = new WeakSet();
    const baseStyles = new Map();
    let active = false;
    const resetActive = (duration) => {
        if (!active)
            return;
        active = false;
        resetHoverItems(hoverItems, baseStyles, duration, transitionEasing);
    };
    hoverItems.forEach((item, itemIndex) => {
        item.triggerElements.forEach((element) => {
            if (element && typeof element === 'object')
                hoverTargets.add(element);
            element.cursor = 'pointer';
            element.silent = false;
            attachHoverHandlers(element, () => {
                if (!isEnabled())
                    return;
                captureBaseStyles(hoverItems, baseStyles);
                active = true;
                applyHoverItem(hoverItems, baseStyles, itemIndex, dimOpacity, transitionDuration, transitionEasing);
            }, () => resetActive(transitionDuration));
        });
    });
    const reset = (eventOrImmediate = false) => {
        const immediate = eventOrImmediate === true;
        resetActive(immediate ? 0 : transitionDuration);
    };
    const handleMove = (event) => {
        if (!active)
            return;
        if (!isHoverTarget(event.target, hoverTargets))
            reset();
    };
    const zrender = options.zrender;
    zrender?.on('mousemove', handleMove);
    zrender?.on('globalout', reset);
    return {
        dispose() {
            zrender?.off('mousemove', handleMove);
            zrender?.off('globalout', reset);
            reset(true);
        }
    };
}
export function setElementHoverBaseStyle(element, style) {
    if (!element || typeof element !== 'object')
        return;
    const target = element;
    if (style == null) {
        delete target[HOVER_BASE_STYLE_KEY];
        return;
    }
    target[HOVER_BASE_STYLE_KEY] = cloneRecord(style);
}
export function setElementHoverDimOpacity(element, opacity) {
    if (!element || typeof element !== 'object')
        return;
    const target = element;
    if (typeof opacity !== 'number' || !Number.isFinite(opacity)) {
        delete target[HOVER_DIM_OPACITY_KEY];
        return;
    }
    target[HOVER_DIM_OPACITY_KEY] = Math.max(0, Math.min(1, opacity));
}
export function setElementHoverEntering(element, entering = true) {
    if (!element || typeof element !== 'object')
        return;
    const target = element;
    if (entering) {
        target[HOVER_ENTERING_KEY] = true;
    }
    else {
        delete target[HOVER_ENTERING_KEY];
    }
}
function captureBaseStyles(items, baseStyles) {
    items.forEach((item) => {
        item.elements.forEach((element) => {
            if (!baseStyles.has(element) || hasExplicitHoverBaseStyle(element)) {
                baseStyles.set(element, cloneStyle(element));
            }
        });
    });
}
function applyHoverItem(items, baseStyles, activeIndex, dimOpacity, duration, easing) {
    const activeElements = new Set(items[activeIndex]?.elements || []);
    items.forEach((item) => {
        item.elements.forEach((element) => {
            if (isHoverEntering(element))
                return;
            const baseStyle = baseStyles.get(element) || {};
            const nextStyle = activeElements.has(element)
                ? cloneRecord(baseStyle)
                : {
                    ...baseStyle,
                    opacity: resolveHoverDimOpacity(element, dimOpacity)
                };
            transitionStyle(element, nextStyle, ['opacity'], duration, easing);
        });
    });
}
function resetHoverItems(items, baseStyles, duration, easing) {
    const seen = new Set();
    items.forEach((item) => {
        item.elements.forEach((element) => {
            if (seen.has(element))
                return;
            seen.add(element);
            if (isHoverEntering(element))
                return;
            transitionStyle(element, cloneRecord(baseStyles.get(element) || {}), ['opacity'], duration, easing);
        });
    });
}
function transitionStyle(element, nextStyle, keys, duration, easing) {
    if (duration <= 0) {
        replaceGraphicStyle(element, nextStyle);
        return;
    }
    const target = createTransitionTarget(nextStyle, keys);
    const animatable = element;
    animatable.stopAnimation?.(HOVER_TRANSITION_SCOPE, false);
    const animator = animatable.animate?.('style');
    if (!animator || !Object.keys(target).length) {
        replaceGraphicStyle(element, nextStyle);
        return;
    }
    animator.scope = HOVER_TRANSITION_SCOPE;
    animator.when(duration, target).done?.(() => replaceGraphicStyle(element, nextStyle));
    animator.start(easing);
}
function createTransitionTarget(nextStyle, keys) {
    const target = {};
    keys.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(nextStyle, key)) {
            target[key] = nextStyle[key];
        }
        else if (key === 'opacity') {
            target[key] = 1;
        }
    });
    return target;
}
function attachHoverHandlers(element, onEnter, onLeave) {
    const evented = element;
    evented.on?.('mouseover', onEnter);
    evented.on?.('mouseout', onLeave);
}
function createEnabledPredicate(enabled) {
    if (typeof enabled === 'function')
        return enabled;
    return () => enabled !== false;
}
function isHoverTarget(target, hoverTargets) {
    let current = target;
    while (current && typeof current === 'object') {
        if (hoverTargets.has(current))
            return true;
        current = current.parent;
    }
    return false;
}
function uniqueElements(elements) {
    if (!Array.isArray(elements))
        return [];
    const result = [];
    const seen = new Set();
    elements.forEach((element) => {
        if (!element || seen.has(element))
            return;
        seen.add(element);
        result.push(element);
    });
    return result;
}
function replaceGraphicStyle(element, style) {
    const target = element;
    const next = cloneRecord(style);
    removeMissingStyleKeys(target.style, next);
    if (typeof target.setStyle === 'function') {
        target.setStyle(next);
    }
    else if (typeof target.attr === 'function') {
        target.attr('style', next);
    }
    else {
        target.style = next;
    }
}
function removeMissingStyleKeys(current, next) {
    if (!current || typeof current !== 'object' || Array.isArray(current))
        return;
    const style = current;
    Object.keys(style).forEach((key) => {
        if (!Object.prototype.hasOwnProperty.call(next, key))
            delete style[key];
    });
}
function cloneStyle(element) {
    const target = element;
    return cloneRecord(asRecord(target[HOVER_BASE_STYLE_KEY] ?? target.style));
}
function hasExplicitHoverBaseStyle(element) {
    return Object.prototype.hasOwnProperty.call(element, HOVER_BASE_STYLE_KEY);
}
function resolveHoverDimOpacity(element, fallback) {
    return finiteNumber(element[HOVER_DIM_OPACITY_KEY], fallback);
}
function cloneRecord(record) {
    return {
        ...record
    };
}
function asRecord(value) {
    return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}
function isHoverEntering(element) {
    return element[HOVER_ENTERING_KEY] === true;
}
function finiteNumber(value, fallback) {
    return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}
export const __test__ = {
    applyHoverItem,
    asRecord,
    cloneStyle,
    createTransitionTarget,
    removeMissingStyleKeys,
    resolveHoverDimOpacity,
    resetHoverItems
};
//# sourceMappingURL=element-hover.js.map